import { X, File, FileText, FileSpreadsheet, FileArchive, Film, Music } from 'lucide-react';
import { formatFileSize } from './useChatAttachments';
import type { PendingFile } from './useChatAttachments';

interface AttachmentPreviewProps {
  files: PendingFile[];
  onRemove: (id: string) => void;
  uploading: boolean;
}

function getFileIcon(type: string) {
  if (type.startsWith('video/')) return Film;
  if (type.startsWith('audio/')) return Music;
  if (type.includes('pdf')) return FileText;
  if (type.includes('spreadsheet') || type.includes('csv') || type.includes('excel')) return FileSpreadsheet;
  if (type.includes('zip') || type.includes('rar') || type.includes('tar') || type.includes('gz')) return FileArchive;
  if (type.includes('document') || type.includes('text')) return FileText;
  return File;
}

function truncateFileName(name: string, maxLen = 20): string {
  if (name.length <= maxLen) return name;
  const ext = name.lastIndexOf('.');
  if (ext > 0) {
    const extension = name.slice(ext);
    const base = name.slice(0, ext);
    const maxBase = maxLen - extension.length - 3;
    if (maxBase > 0) {
      return `${base.slice(0, maxBase)}...${extension}`;
    }
  }
  return name.slice(0, maxLen - 3) + '...';
}

export default function AttachmentPreview({ files, onRemove, uploading }: AttachmentPreviewProps) {
  if (files.length === 0) return null;

  return (
    <div
      className="px-3 pt-2 pb-1"
      style={{ borderTop: '1px solid var(--border-hairline)' }}
    >
      {/* Upload progress bar */}
      {uploading && (
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-1">
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
              Uploading {files.length} file{files.length > 1 ? 's' : ''}...
            </span>
          </div>
          <div
            className="h-1 overflow-hidden"
            style={{ background: 'var(--surface-raised)', borderRadius: 'var(--radius-pill)' }}
          >
            <div
              className="h-full animate-pulse w-full"
              style={{ background: 'var(--accent)', borderRadius: 'var(--radius-pill)' }}
            />
          </div>
        </div>
      )}

      {/* File previews */}
      <div className="flex flex-wrap gap-2">
        {files.map((pf) => {
          const isImage = pf.file.type.startsWith('image/');
          const FileIcon = getFileIcon(pf.file.type);

          return (
            <div
              key={pf.id}
              className={`relative group overflow-hidden flex-shrink-0 ${
                isImage ? 'w-20 h-20' : 'w-40'
              }`}
              style={{
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {/* Remove button */}
              {!uploading && (
                <button
                  onClick={() => onRemove(pf.id)}
                  className="absolute top-1 right-1 z-10 p-0.5 transition-opacity opacity-0 group-hover:opacity-100 touch-reveal pressable"
                  style={{
                    background: 'var(--surface-overlay)',
                    color: 'var(--text-body)',
                    border: 'none',
                    borderRadius: 'var(--radius-pill)',
                    cursor: 'pointer',
                  }}
                  title="Remove file"
                >
                  <X className="w-3 h-3" />
                </button>
              )}

              {isImage && pf.preview ? (
                /* Image thumbnail */
                <img
                  src={pf.preview}
                  alt={pf.file.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                /* File info card */
                <div className="flex items-center gap-2 px-2.5 py-2">
                  <FileIcon
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: 'var(--text-faint)' }}
                  />
                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate"
                      style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                        color: 'var(--text-body)',
                      }}
                      title={pf.file.name}
                    >
                      {truncateFileName(pf.file.name)}
                    </div>
                    <div
                      className="t-num"
                      style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}
                    >
                      {formatFileSize(pf.file.size)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
